/**
 * Orchestrator: takes a user intent + optional model override, runs the
 * director, dispatches to the chosen provider, persists the task to IDB,
 * and exposes a polling/streaming helper.
 */
import { nanoid } from 'nanoid';
import { decide, type DirectorDecision } from './director';
import { PROVIDERS, modelById } from '../providers';
import { db } from '../lib/db';
import { getKey } from '../lib/keys';
import type { GenerateRequest, GenerateResult } from './types';
import type { Provider } from '../lib/db';

export interface OrchestrationInput extends GenerateRequest {
  modelId?: string;            // explicit override
  sessionId?: string;
  /** If true, the director will not be called (use raw prompt as-is). */
  skipDirector?: boolean;
}

export interface OrchestrationStart {
  taskId: string;
  decision: DirectorDecision;
  provider: Provider;
  modelId: string;
  initial: GenerateResult;
}

export async function startGeneration(input: OrchestrationInput): Promise<OrchestrationStart> {
  const decision = input.skipDirector
    ? fallbackDecision(input)
    : await decide(input);

  const chosen = input.modelId
    ? { modelId: input.modelId, provider: (modelById(input.modelId)?.provider ?? decision.recommended.provider) }
    : decision.recommended;

  const adapter = PROVIDERS[chosen.provider];
  if (!adapter) throw new Error(`Unknown provider ${chosen.provider}`);

  const key = await getKey(chosen.provider);
  if (!key) throw new Error(`Missing API key for ${chosen.provider}. Add it in Settings → API keys.`);

  const finalReq: GenerateRequest = {
    ...input,
    prompt: decision.brief || input.prompt,
    negativePrompt: input.negativePrompt ?? decision.negative
  };

  const localId = nanoid();
  const d = await db();
  await d.put('tasks', {
    id: localId,
    provider: chosen.provider,
    kind: input.mode === 'video' ? 'video' : 'image',
    status: 'queued',
    prompt: finalReq.prompt,
    request: finalReq,
    sessionId: input.sessionId,
    createdAt: Date.now(),
    updatedAt: Date.now()
  });

  let initial: GenerateResult;
  try {
    initial = await adapter.generate(chosen.modelId, finalReq, key);
  } catch (e: any) {
    await d.put('tasks', {
      ...((await d.get('tasks', localId))!),
      status: 'failed',
      error: e?.message ?? String(e),
      updatedAt: Date.now()
    });
    throw e;
  }

  await d.put('tasks', {
    ...((await d.get('tasks', localId))!),
    remoteId: initial.taskId,
    status: initial.status === 'succeeded' ? 'succeeded' : initial.status === 'failed' ? 'failed' : 'running',
    response: initial,
    error: initial.error,
    updatedAt: Date.now()
  });

  return { taskId: localId, decision, provider: chosen.provider, modelId: chosen.modelId, initial };
}

export async function pollUntilDone(localTaskId: string, opts?: { intervalMs?: number; signal?: AbortSignal }): Promise<GenerateResult> {
  const interval = opts?.intervalMs ?? 4000;
  const d = await db();
  while (true) {
    if (opts?.signal?.aborted) throw new Error('aborted');
    const t = await d.get('tasks', localTaskId);
    if (!t) throw new Error('task not found');
    if (t.status === 'succeeded' || t.status === 'failed' || t.status === 'cancelled') {
      return t.response as GenerateResult;
    }
    const adapter = PROVIDERS[t.provider];
    if (!adapter.poll || !t.remoteId) {
      // Synchronous provider with no poll path; assume terminal.
      return t.response as GenerateResult;
    }
    const key = await getKey(t.provider);
    if (!key) throw new Error(`Missing key for ${t.provider}`);
    const res = await adapter.poll(t.remoteId, key);
    await d.put('tasks', {
      ...t,
      status: res.status === 'succeeded' ? 'succeeded' : res.status === 'failed' ? 'failed' : 'running',
      response: res,
      error: res.error,
      updatedAt: Date.now()
    });
    if (res.status === 'succeeded' || res.status === 'failed') return res;
    await new Promise(r => setTimeout(r, interval));
  }
}

function fallbackDecision(req: GenerateRequest): DirectorDecision {
  return {
    brief: req.prompt,
    negative: req.negativePrompt ?? '',
    recommended: {
      provider: req.mode === 'video' ? 'byteplus' : 'gemini',
      modelId: req.mode === 'video' ? 'dreamina-seedance-2-0-260128' : 'imagen-4',
      reason: 'fallback default'
    },
    alternates: []
  };
}
